import {useAuthStore} from "@/store/authStore";
import {useUiStore} from "@/store/uiStore";
import {useLocation} from "react-router";

const ROUTE_LABELS: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/users": "Users",
  "/transactions": "Transactions",
  "/transfers": "Transfers & Remittances",
  "/remittances": "Remittances",
  "/kyc": "KYC & Compliance",
  "/exchange-rates": "Exchange Rates",
  "/payment-methods": "Payment Methods",
  "/countries": "Countries",
  "/wallets": "Wallets",
  "/commissions": "Commissions",
  "/settings": "Settings",
};

export const Header = () => {
  const {pathname} = useLocation();
  const logout = useAuthStore(s => s.logout);
  const user = useAuthStore(s => s.user);
  const toggleSidebar = useUiStore(s => s.toggleSidebar);

  const title = ROUTE_LABELS[pathname] ?? "Backoffice";
  const initials =
    user?.fullName
      .split(" ")
      .slice(0, 2)
      .map(n => n[0])
      .join("")
      .toUpperCase() ?? "A";

  return (
    <header
      className="flex items-center justify-between px-6 bg-white border-b border-(--color-border) shrink-0"
      style={{height: "var(--header-height)"}}
    >
      {/* Left */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={toggleSidebar}
          className="p-1.5 rounded-md text-(--color-text-secondary) hover:bg-(--color-surface-2) hover:text-(--color-text) transition-colors"
          aria-label="Toggle sidebar"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line
              x1="3"
              y1="6"
              x2="17"
              y2="6"
            />
            <line
              x1="3"
              y1="10"
              x2="17"
              y2="10"
            />
            <line
              x1="3"
              y1="14"
              x2="17"
              y2="14"
            />
          </svg>
        </button>
        <h1 className="text-lg font-semibold text-(--color-text)">{title}</h1>
      </div>

      {/* Right */}
      <div className="flex items-center gap-3">
        {/* Notifications */}
        <button
          type="button"
          className="relative p-2 rounded-md text-(--color-text-secondary) hover:bg-(--color-surface-2) transition-colors"
          aria-label="Notifications"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-(--color-secondary) rounded-full" />
        </button>

        {/* Divider */}
        <div className="w-px h-6 bg-(--color-border)" />

        {/* User */}
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
            style={{backgroundColor: "var(--color-primary)"}}
          >
            {initials}
          </div>
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-(--color-text) leading-none">{user?.fullName ?? "Admin"}</p>
            <p className="text-xs text-(--color-text-tertiary) capitalize">{user?.role ?? "admin"}</p>
          </div>
        </div>

        {/* Logout */}
        <button
          type="button"
          onClick={logout}
          title="Logout"
          className="p-2 rounded-md text-(--color-text-secondary) hover:bg-(--color-surface-2) hover:text-(--color-danger) transition-colors"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line
              x1="21"
              y1="12"
              x2="9"
              y2="12"
            />
          </svg>
        </button>
      </div>
    </header>
  );
};
